import { useEffect, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Ticket,
  Search,
  LogOut,
  Clock,
  CheckCircle2,
  AlertCircle,
  Loader2,
  X,
  Mail,
  Building2,
  Globe,
  Monitor,
  FileText,
  ExternalLink,
  ImageIcon,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { selectData, updateTicket } from '../lib/supabase';
import { useAdminAuth } from '../contexts/AdminAuthContext';

const STATUSES = [
  { value: 'new', label: 'Nouveau', color: 'bg-blue-500' },
  { value: 'in_progress', label: 'En cours', color: 'bg-orange-500' },
  { value: 'resolved', label: 'Résolu', color: 'bg-green-500' },
  { value: 'closed', label: 'Fermé', color: 'bg-gray-500' },
];

const PRIORITIES = {
  basse: { label: '🟢 Basse', color: 'text-green-600' },
  normale: { label: '🟡 Normale', color: 'text-yellow-600' },
  haute: { label: '🟠 Haute', color: 'text-orange-600' },
  critique: { label: '🔴 Critique', color: 'text-red-600' },
};

export const AdminTickets = () => {
  const { adminProfile, logout } = useAdminAuth();
  const [tickets, setTickets] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [notes, setNotes] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const loadTickets = async () => {
    setIsLoading(true);
    const { data, error } = await selectData('tickets', {
      order: { column: 'created_at', ascending: false },
    });
    if (error) {
      toast.error('Erreur de chargement des tickets');
    } else {
      setTickets(data);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const stats = useMemo(
    () => ({
      total: tickets.length,
      new: tickets.filter((t) => t.status === 'new').length,
      in_progress: tickets.filter((t) => t.status === 'in_progress').length,
      resolved: tickets.filter((t) => t.status === 'resolved').length,
    }),
    [tickets],
  );

  const filteredTickets = useMemo(() => {
    return tickets.filter((t) => {
      if (statusFilter !== 'all' && t.status !== statusFilter) return false;
      if (priorityFilter !== 'all' && t.priority !== priorityFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        return (
          t.subject?.toLowerCase().includes(q) ||
          t.first_name?.toLowerCase().includes(q) ||
          t.last_name?.toLowerCase().includes(q) ||
          t.email?.toLowerCase().includes(q) ||
          t.company?.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [tickets, statusFilter, priorityFilter, search]);

  const openTicket = (ticket) => {
    setSelectedTicket(ticket);
    setNotes(ticket.admin_notes || '');
  };

  const closeModal = () => {
    setSelectedTicket(null);
    setNotes('');
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    setIsSaving(true);
    const updates = {
      status: newStatus,
      resolved_at: newStatus === 'resolved' ? new Date().toISOString() : null,
    };
    const { error } = await updateTicket(selectedTicket.id, updates);
    if (error) {
      toast.error('Erreur lors de la mise à jour');
    } else {
      toast.success('Statut mis à jour');
      setSelectedTicket({ ...selectedTicket, ...updates });
      setTickets((prev) =>
        prev.map((t) => (t.id === selectedTicket.id ? { ...t, ...updates } : t)),
      );
    }
    setIsSaving(false);
  };

  const handleSaveNotes = async () => {
    if (!selectedTicket) return;
    setIsSaving(true);
    const { error } = await updateTicket(selectedTicket.id, {
      admin_notes: notes,
    });
    if (error) {
      toast.error("Erreur d'enregistrement");
    } else {
      toast.success('Notes enregistrées');
      setTickets((prev) =>
        prev.map((t) =>
          t.id === selectedTicket.id ? { ...t, admin_notes: notes } : t,
        ),
      );
    }
    setIsSaving(false);
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('fr-FR', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusMeta = (status) =>
    STATUSES.find((s) => s.value === status) || STATUSES[0];

  return (
    <>
      <Helmet>
        <title>Tickets | Administration Weblax</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header admin */}
        <header className="border-b border-border bg-card sticky top-0 z-40">
          <div className="weblax-container flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                <Ticket className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="font-semibold text-sm">Administration</p>
                <p className="text-xs text-muted-foreground">
                  {adminProfile?.first_name} {adminProfile?.last_name} ·{' '}
                  {adminProfile?.role === 'super_admin' ? 'Super admin' : 'Admin'}
                </p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={logout}>
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </header>

        <div className="weblax-container py-8">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="weblax-card">
              <CardContent className="p-5">
                <p className="text-xs text-muted-foreground mb-1">Total</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </CardContent>
            </Card>
            <Card className="weblax-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-3 h-3 text-blue-500" />
                  <p className="text-xs text-muted-foreground">Nouveaux</p>
                </div>
                <p className="text-2xl font-bold text-blue-500">{stats.new}</p>
              </CardContent>
            </Card>
            <Card className="weblax-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-3 h-3 text-orange-500" />
                  <p className="text-xs text-muted-foreground">En cours</p>
                </div>
                <p className="text-2xl font-bold text-orange-500">{stats.in_progress}</p>
              </CardContent>
            </Card>
            <Card className="weblax-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-3 h-3 text-green-500" />
                  <p className="text-xs text-muted-foreground">Résolus</p>
                </div>
                <p className="text-2xl font-bold text-green-500">{stats.resolved}</p>
              </CardContent>
            </Card>
          </div>

          {/* Filtres */}
          <Card className="weblax-card mb-6">
            <CardContent className="p-4 flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher (objet, client, email...)"
                  className="weblax-input pl-10"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="weblax-input w-full md:w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {STATUSES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>
                      {s.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={setPriorityFilter}>
                <SelectTrigger className="weblax-input w-full md:w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes priorités</SelectItem>
                  {Object.entries(PRIORITIES).map(([v, p]) => (
                    <SelectItem key={v} value={v}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardContent>
          </Card>

          {/* Liste tickets */}
          {isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredTickets.length === 0 ? (
            <Card className="weblax-card">
              <CardContent className="p-12 text-center">
                <Ticket className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                <p className="text-muted-foreground">Aucun ticket trouvé</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {filteredTickets.map((t) => {
                const statusMeta = getStatusMeta(t.status);
                const priority = PRIORITIES[t.priority] || PRIORITIES.normale;
                return (
                  <Card
                    key={t.id}
                    onClick={() => openTicket(t)}
                    className="weblax-card cursor-pointer hover:border-primary/50 transition-all"
                  >
                    <CardContent className="p-4 flex items-center gap-4">
                      <div
                        className={`w-2 h-14 rounded-full ${statusMeta.color} flex-shrink-0`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <p className={`text-xs font-medium ${priority.color}`}>
                            {priority.label}
                          </p>
                          <span className="text-muted-foreground text-xs">·</span>
                          <p className="text-xs text-muted-foreground">
                            {statusMeta.label}
                          </p>
                          {t.screenshot_urls?.length > 0 && (
                            <>
                              <span className="text-muted-foreground text-xs">·</span>
                              <span className="text-xs text-muted-foreground flex items-center gap-1">
                                <ImageIcon className="w-3 h-3" />
                                {t.screenshot_urls.length}
                              </span>
                            </>
                          )}
                        </div>
                        <p className="font-semibold truncate">{t.subject}</p>
                        <p className="text-sm text-muted-foreground truncate">
                          {t.first_name} {t.last_name}
                          {t.company && ` · ${t.company}`}
                          {t.email && ` · ${t.email}`}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground whitespace-nowrap hidden sm:block">
                        {formatDate(t.created_at)}
                      </p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal détail */}
        <Dialog open={!!selectedTicket} onOpenChange={(o) => !o && closeModal()}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            {selectedTicket && (
              <>
                <DialogHeader>
                  <div className="flex items-start justify-between gap-4 pr-6">
                    <div>
                      <p
                        className={`text-xs font-medium mb-1 ${
                          PRIORITIES[selectedTicket.priority]?.color || ''
                        }`}
                      >
                        {PRIORITIES[selectedTicket.priority]?.label}
                      </p>
                      <DialogTitle className="text-xl">
                        {selectedTicket.subject}
                      </DialogTitle>
                      <p className="text-xs text-muted-foreground mt-1">
                        Créé le {formatDate(selectedTicket.created_at)}
                      </p>
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                  {/* Statut */}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Statut
                    </Label>
                    <div className="flex gap-2 mt-2 flex-wrap">
                      {STATUSES.map((s) => (
                        <Button
                          key={s.value}
                          size="sm"
                          variant={
                            selectedTicket.status === s.value ? 'default' : 'outline'
                          }
                          onClick={() => handleStatusChange(s.value)}
                          disabled={isSaving}
                        >
                          <div className={`w-2 h-2 rounded-full ${s.color} mr-2`} />
                          {s.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Infos client */}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Client
                    </Label>
                    <div className="mt-2 space-y-2 text-sm">
                      <p className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        
                          <a href={`mailto:${selectedTicket.email}?subject=Re: [Ticket] ${selectedTicket.subject}`}
                          className="text-primary hover:underline"
                        >
                          {selectedTicket.first_name} {selectedTicket.last_name} —{' '}
                          {selectedTicket.email}
                        </a>
                      </p>
                      {selectedTicket.company && (
                        <p className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-muted-foreground" />
                          {selectedTicket.company}
                        </p>
                      )}
                      {selectedTicket.website && (
                        <p className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-muted-foreground" />
                          <a
                            href={selectedTicket.website}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary hover:underline flex items-center gap-1"
                          >
                            {selectedTicket.website}
                            <ExternalLink className="w-3 h-3" />
                          </a>
                        </p>
                      )}
                      {selectedTicket.browser && (
                        <p className="flex items-center gap-2">
                          <Monitor className="w-4 h-4 text-muted-foreground" />
                          {selectedTicket.browser}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Description
                    </Label>
                    <div className="mt-2 p-4 bg-muted rounded-lg text-sm whitespace-pre-wrap">
                      {selectedTicket.description}
                    </div>
                  </div>

                  {/* Étapes */}
                  {selectedTicket.steps_to_reproduce && (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Étapes pour reproduire
                      </Label>
                      <div className="mt-2 p-4 bg-muted rounded-lg text-sm whitespace-pre-wrap font-mono">
                        {selectedTicket.steps_to_reproduce}
                      </div>
                    </div>
                  )}

                  {/* Captures */}
                  {selectedTicket.screenshot_urls?.length > 0 && (
                    <div>
                      <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                        Captures d'écran ({selectedTicket.screenshot_urls.length})
                      </Label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2">
                        {selectedTicket.screenshot_urls.map((url, i) => (
                          <a
                            key={i}
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            className="block border border-border rounded-lg overflow-hidden hover:border-primary transition-colors"
                          >
                            <img
                              src={url}
                              alt={`Capture ${i + 1}`}
                              className="w-full h-32 object-cover"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Notes internes */}
                  <div>
                    <Label
                      htmlFor="admin-notes"
                      className="text-xs uppercase tracking-wider text-muted-foreground"
                    >
                      Notes internes (non visibles par le client)
                    </Label>
                    <Textarea
                      id="admin-notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Ajoutez vos notes de résolution, actions effectuées..."
                      className="weblax-input mt-2 min-h-[100px]"
                    />
                    <Button
                      onClick={handleSaveNotes}
                      disabled={isSaving}
                      size="sm"
                      className="mt-2"
                    >
                      {isSaving ? 'Enregistrement...' : 'Enregistrer les notes'}
                    </Button>
                  </div>

                  {/* Actions rapides */}
                  <div className="flex gap-2 pt-4 border-t border-border">
                    <Button asChild variant="outline" className="flex-1">
                      
                        <a href={`mailto:${selectedTicket.email}?subject=Re: [Ticket] ${selectedTicket.subject}`}
                      >
                        <Mail className="w-4 h-4 mr-2" />
                        Répondre au client
                      </a>
                    </Button>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default AdminTickets;